/**
 * Export Service
 * API calls for data export functionality
 */

import { api } from './api';

export type ExportType = 'objectives' | 'key_results' | 'both' | 'users';
export type ExportFormat = 'excel' | 'csv';

export interface ExportRequest {
  type: ExportType;
  format: ExportFormat;
}

/**
 * Export data and trigger download
 * @param type - Type of data to export
 * @param format - Format (excel or csv)
 * @returns Promise that resolves when download is triggered
 */
export const exportData = async (type: ExportType, format: ExportFormat): Promise<void> => {
  try {
    const response = await api.post(
      '/export',
      { type, format },
      {
        responseType: 'blob', // Important for file downloads
      }
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `export_${type}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }

    // Create blob and trigger download
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

/**
 * Export objectives
 * @param format - Format (excel or csv)
 */
export const exportObjectives = async (format: ExportFormat): Promise<void> => {
  return exportData('objectives', format);
};

/**
 * Export key results
 * @param format - Format (excel or csv)
 */
export const exportKeyResults = async (format: ExportFormat): Promise<void> => {
  return exportData('key_results', format);
};

/**
 * Export both objectives and key results (Excel only)
 * @param format - Format (excel only for this type)
 */
export const exportBoth = async (format: ExportFormat = 'excel'): Promise<void> => {
  return exportData('both', format);
};

/**
 * Export users
 * @param format - Format (excel or csv)
 */
export const exportUsers = async (format: ExportFormat): Promise<void> => {
  return exportData('users', format);
};

export const exportService = {
  exportData,
  exportObjectives,
  exportKeyResults,
  exportBoth,
  exportUsers,
};

export default exportService;
